import { Popover } from "@mantine/core";
import { ReactNode, useState } from "react";

import { Icon } from "~/assets/icons/Icon";
import Button from "~/components/mantine/button/Button";

import styles from "./ActionColumn.module.scss";

/**
 * Standard three-dots action button with a Mantine Popover dropdown.
 * Designed to be used inside DataTable action columns.
 *
 * Usage:
 * ```tsx
 * {
 *   id: "actions",
 *   header: "",
 *   size: 50,
 *   enableSorting: false,
 *   cell: (info) => (
 *     <Actions>
 *       {(close) => (
 *         <>
 *           <Button variant="menuItem" onClick={() => { doThing(); close(); }}>Edit</Button>
 *           <Button variant="menuItem" onClick={...}>Delete</Button>
 *         </>
 *       )}
 *     </Actions>
 *   ),
 * }
 * ```
 */
export const ActionColumn = ({
  children,
  disabled,
}: {
  children: ReactNode | ((close: () => void) => ReactNode);
  disabled?: boolean;
}) => {
  const [opened, setOpened] = useState(false);
  const close = () => setOpened(false);

  return (
    <div className={styles.actionButton}>
      <Popover
        offset={1}
        position="bottom"
        withinPortal={true}
        opened={opened}
        onChange={setOpened}
        zIndex={1000}
        styles={{
          dropdown: {
            minWidth: "150px",
          },
        }}
      >
        <Popover.Target>
          <Button
            variant="unstyled"
            disabled={disabled}
            onClick={() => setOpened((o) => !o)}
          >
            <Icon iconName="TreeDotsButtonIcon" width="1rem" height="1rem" />
          </Button>
        </Popover.Target>
        <Popover.Dropdown className={styles.dropdown}>
          {typeof children === "function" ? children(close) : children}
        </Popover.Dropdown>
      </Popover>
    </div>
  );
};
